import { useState } from "react";
import { toast } from "sonner";
import { Trash2, ClipboardList, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, API } from "@/lib/api";

const DEPT_LABELS = { cucina: "Cucina", sala_bar: "Sala / Bar", pulizie: "Pulizie" };

const SheetEditor = ({ sheet, tasks, setTasks, onSave }) => (
  <div data-testid={`sheet-editor-${sheet.department}`} className="mt-3 space-y-2 border-t border-slate-700 pt-3">
    {tasks.map((t, i) => (
      <div key={t.id || i} className="flex gap-1.5 items-start">
        <Input data-testid={`sheet-task-time-${i}`} className="w-20 h-8 bg-[#0F172A] border-slate-700 text-white text-xs" placeholder="Ora" value={t.time || ""} onChange={(e) => setTasks(tasks.map((x, j) => (j === i ? { ...x, time: e.target.value } : x)))} />
        <div className="flex-1 space-y-1">
          <Input data-testid={`sheet-task-title-${i}`} className="h-8 bg-[#0F172A] border-slate-700 text-white text-xs" placeholder="Mansione" value={t.title} onChange={(e) => setTasks(tasks.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
          <Input data-testid={`sheet-task-detail-${i}`} className="h-8 bg-[#0F172A] border-slate-700 text-slate-300 text-xs" placeholder="Dettaglio / quantità (a mano)" value={t.detail || ""} onChange={(e) => setTasks(tasks.map((x, j) => (j === i ? { ...x, detail: e.target.value } : x)))} />
        </div>
        <button onClick={() => setTasks(tasks.filter((_, j) => j !== i))} className="text-slate-500 hover:text-rose-400 mt-1.5"><Trash2 className="w-4 h-4" /></button>
      </div>
    ))}
    <div className="flex gap-2">
      <Button size="sm" variant="outline" className="border-slate-600 text-slate-900 h-7 text-xs" onClick={() => setTasks([...tasks, { time: "", title: "", detail: "", done: false }])}>+ Mansione</Button>
      <Button data-testid={`save-sheet-tasks-${sheet.department}`} size="sm" className="bg-[#C85A32] hover:bg-[#a94a28] h-7 text-xs" onClick={onSave}>Salva scheda</Button>
    </div>
  </div>
);

export const SheetsPanel = ({ quoteId, action, sheets, setSheets }) => {
  const [editSheet, setEditSheet] = useState(null);
  const [sheetTasks, setSheetTasks] = useState([]);

  const generate = () => {
    if (sheets.length && !window.confirm("Rigenerare le schede? I progressi già spuntati dai reparti verranno azzerati e i vecchi link non funzioneranno più.")) return;
    action(async () => { const r = await api.post(`/admin/quotes/${quoteId}/generate-sheets`); setSheets(r.data); }, "Schede generate per i 3 reparti");
  };

  const saveTasks = (s) => action(async () => {
    const r = await api.put(`/admin/sheets/${s.id}/tasks`, { tasks: sheetTasks.filter((t) => t.title) });
    setSheets(sheets.map((x) => (x.id === s.id ? r.data : x)));
    setEditSheet(null);
  }, "Scheda aggiornata");

  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-widest text-[#C85A32] mb-2">Schede operative reparti</h3>
      <Button data-testid="generate-sheets-button" className="bg-[#4A6B5D] hover:bg-[#3a5549] mb-3" onClick={generate}>
        <ClipboardList className="w-4 h-4 mr-1" /> {sheets.length ? "Rigenera schede" : "Genera schede reparti"}
      </Button>
      <div className="space-y-2">
        {sheets.map((s) => {
          const done = s.tasks.filter((t) => t.done).length;
          const pct = s.tasks.length ? Math.round((done / s.tasks.length) * 100) : 0;
          const link = `${window.location.origin}/scheda/${s.token}`;
          return (
            <div key={s.id} data-testid={`sheet-card-${s.department}`} className="bg-[#1E293B] rounded-lg p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-semibold text-sm">{DEPT_LABELS[s.department]}</div>
                <div className="flex items-center gap-2">
                  <span data-testid={`sheet-progress-${s.department}`} className="text-xs font-mono-num text-emerald-400">{pct}% · {done}/{s.tasks.length}</span>
                  <button data-testid={`edit-sheet-${s.department}`} onClick={() => { if (editSheet === s.id) { setEditSheet(null); } else { setEditSheet(s.id); setSheetTasks(s.tasks.map((t) => ({ ...t }))); } }} className="text-xs text-[#F0B429] hover:underline">
                    {editSheet === s.id ? "Chiudi" : "Modifica"}
                  </button>
                  <button data-testid={`copy-sheet-link-${s.department}`} onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copiato — invialo al caporeparto via WhatsApp"); }} className="text-slate-400 hover:text-white flex items-center gap-1 text-xs">
                    <Copy className="w-3.5 h-3.5" /> Copia link
                  </button>
                  <a href={link} target="_blank" rel="noreferrer" className="text-xs text-[#F0B429] hover:underline">Apri</a>
                  <a data-testid={`sheet-pdf-${s.department}`} href={`${API}/sheets/${s.token}/pdf`} target="_blank" rel="noreferrer"
                    className="text-xs font-bold bg-[#C85A32] text-white px-2.5 py-1 rounded-full">PDF</a>
                </div>
              </div>
              {editSheet === s.id && <SheetEditor sheet={s} tasks={sheetTasks} setTasks={setSheetTasks} onSave={() => saveTasks(s)} />}
              <div className="h-1.5 bg-slate-700 rounded-full mt-2 overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
